import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { isAdminOrPlatform } from '../lib/access';
import { useLocation, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Search, FileText, Mail, AlertCircle, MessageCircle, Plus, RefreshCw, X, ExternalLink, Clock, User, Tag, Building2, Flag, Info, CheckCircle, ArrowRight, ChevronRight, ChevronDown, ChevronUp, AlertTriangle, MessageSquare, HelpCircle, Globe, Lock, Lightbulb } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import StructuredResponse from '../components/StructuredResponse';
import DecisionResponse from '../components/DecisionResponse';
import QueryFeedback from '../components/QueryFeedback';
import ResolutionConfirmation from '../components/ResolutionConfirmation';

// Citation regex pattern: matches [XX-###] format
const CITATION_PATTERN = /\[([A-Z]{2}-\d{3})\]/g;

// ============= NO ANSWER STATE COMPONENT =============
const NoAnswerState = ({
  results,
  queryMode,
  webEnabled,
  onFlagClick,
  onFloorSpaceClick,
  onWebSearch,
}) => {
  if (!results?.no_internal_source) return null;

  const searchedScope = queryMode === 'company_verification'
    ? 'Qflow searched the configured web sources, but nothing matched closely enough.'
    : queryMode === 'internal_and_web'
      ? 'Qflow searched your accessible internal knowledge and the configured web sources, but nothing matched closely enough.'
      : 'Qflow searched your accessible internal knowledge, but nothing matched closely enough.';

  const actionRows = [
    {
      id: 'floor-space',
      visible: true,
      title: 'Ask on Floor Space',
      description: 'Get live help from a colleague or team without losing your question.',
      icon: MessageSquare,
      onClick: onFloorSpaceClick,
      testId: 'no-answer-floor-space-btn',
      accent: true,
    },
    {
      id: 'web-search',
      visible: webEnabled && queryMode === 'standard',
      title: 'Search with web sources',
      description: 'Retry this question using the external providers enabled for your organisation.',
      icon: Globe,
      onClick: onWebSearch,
      testId: 'no-answer-web-search-btn',
    },
    {
      id: 'knowledge-addition',
      visible: true,
      title: 'Flag for knowledge addition',
      description: 'Request that approved internal guidance is created for this topic.',
      icon: Flag,
      onClick: onFlagClick,
      testId: 'no-answer-flag-btn',
    },
  ].filter(action => action.visible);

  return (
    <div className="space-y-5" data-testid="no-answer-state">
      <Card className="overflow-hidden border border-slate-200 bg-white shadow-none">
        <div className="px-5 py-5 sm:px-6">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
              <HelpCircle size={18} className="text-slate-500" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-slate-900" data-testid="no-answer-title">
                No documented guidance for this yet
              </h2>
              <p className="mt-1 text-sm leading-6 text-slate-600">{searchedScope}</p>
            </div>
          </div>
        </div>
      </Card>

      <section aria-labelledby="alternative-answer-heading">
        <h3 id="alternative-answer-heading" className="mb-2 text-sm font-semibold text-slate-800">
          Or get an answer another way
        </h3>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {actionRows.map((action, index) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                type="button"
                onClick={action.onClick}
                data-testid={action.testId}
                className={`group flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-500 ${index ? 'border-t border-slate-200' : ''}`}
              >
                <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${action.accent ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-600'}`}>
                  <Icon size={18} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-slate-900">{action.title}</span>
                  <span className="mt-0.5 block text-xs leading-5 text-slate-600">{action.description}</span>
                </span>
                <ChevronRight size={17} className="flex-shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
};

// ============= ACCESS RESTRICTED STATE COMPONENT =============
const AccessRestrictedState = ({ onFloorSpaceClick }) => (
  <Card className="overflow-hidden border-rose-200" data-testid="access-restricted-state">
    <div className="px-6 py-5 bg-rose-50 border-b border-rose-200">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-rose-200 flex items-center justify-center flex-shrink-0">
          <Lock size={20} className="text-rose-600" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-rose-800" data-testid="access-restricted-title">
            Restricted Content
          </h2>
          <p className="text-sm text-rose-600 mt-0.5">
            Relevant guidance exists but is restricted to specific roles or teams.
          </p>
        </div>
      </div>
    </div>
    <div className="p-6 space-y-4">
      <p className="text-sm text-slate-600">
        Contact your team lead or admin to request access, or ask on Floor Space for assistance.
      </p>
      <button
        onClick={onFloorSpaceClick}
        data-testid="access-restricted-floor-space-btn"
        className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-sky-200 bg-sky-50 hover:border-sky-400 hover:bg-sky-100 transition-all text-sm font-medium text-sky-800"
      >
        <MessageSquare size={16} className="text-sky-600" />
        Ask on Floor Space
      </button>
    </div>
  </Card>
);

// ============= WEB SOURCE ATTRIBUTION COMPONENT =============
const WebSourceAttribution = ({
  results,
  onFlagClick,
  onSaveDraft,
  knowledgeUpdatePolicy,
}) => {
  if (!results?.is_web_response) return null;
  const gapDetected = results.web_gap_detected === true;
  const automaticDraft = knowledgeUpdatePolicy === 'auto_draft' && gapDetected;

  return (
    <div className="mt-4 space-y-3" data-testid="web-source-attribution">
      {automaticDraft && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="automatic-web-draft-status">
          {results.knowledge_draft?.id
            ? 'A cited Knowledge Hub note was created as a pending review draft. It will not become searchable until a reviewer approves it.'
            : 'Automatic review drafts are enabled. Eligible web-only knowledge gaps are sent to the review queue and are never published directly.'}
        </div>
      )}
      {/* Actions row */}
      <div className="flex items-center gap-2 flex-wrap">
        {onSaveDraft && (
          <button
            onClick={onSaveDraft}
            data-testid="web-save-draft-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
          >
            <Plus size={12} />
            Save as Internal Draft
          </button>
        )}
        <button
          onClick={onFlagClick}
          data-testid="web-source-flag-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
        >
          <Flag size={12} />
          Request Internal Docs
        </button>
      </div>
    </div>
  );
};

const comparisonStopwords = new Set([
  'the', 'and', 'for', 'with', 'that', 'this', 'from', 'into', 'your', 'you',
  'are', 'was', 'were', 'will', 'can', 'could', 'should', 'would', 'must',
  'have', 'has', 'had', 'not', 'but', 'what', 'when', 'where', 'how', 'why',
  'who', 'which', 'then', 'than', 'also', 'only', 'more', 'less', 'about',
]);

const tokenizeForComparison = (text = '') => (
  (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter(token => token.length > 2 && !comparisonStopwords.has(token))
);

const getResponseText = (results) => {
  if (!results) return '';
  if (results.decision_response?.direct_answer) return results.decision_response.direct_answer;
  if (results.structured_response?.answer) return results.structured_response.answer;
  if (results.structured_response?.summary) return results.structured_response.summary;
  return results.answer || results.ai_suggestions || '';
};

const getInternalSourceText = (sources = []) => sources
  .map(source => [
    source.title,
    source.description,
    source.content,
    source.answer,
    source.resolution,
    source.excerpt,
    source.matched_text,
  ].filter(Boolean).join(' '))
  .join(' ');

// ============= EXTERNAL / INTERNAL DIFFERENTIATOR COMPONENT =============
// Purpose (noise-reduced): ONLY appears in Web mode WHEN Qflow also found a
// matching INTERNAL source. It tells the user "you searched the web — here is
// what your internal source actually says" and shows the refined internal answer.
// When there is NO internal match, it renders nothing (silence > noise).
const ExternalInternalDifferentiator = ({ results, onFlagClick, onSaveDraft }) => {
  if (!results?.is_web_response || results?.is_combined_response) return null;

  const comparison = results.internal_external_comparison;
  const backendSources = comparison?.internal_sources || [];
  const internalSources = backendSources.length
    ? backendSources
    : (results.matched_resolutions || []).filter(source => !source.url && !source.web_url);

  // Only surface when internal guidance genuinely exists.
  const hasInternal = comparison
    ? comparison.internal_available === true
    : internalSources.length > 0;
  const internalExcerpt = (comparison?.internal_answer_excerpt || '').trim();

  // Noise reduction: no internal match → show nothing at all.
  if (!hasInternal || !internalExcerpt) return null;

  const confidencePct = comparison?.internal_confidence
    ? Math.round(comparison.internal_confidence * 100)
    : null;
  const sourceNames = internalSources.slice(0, 2).map(s => s.title).filter(Boolean).join(', ');
  const extraCount = internalSources.length > 2 ? internalSources.length - 2 : 0;

  return (
    <div
      className="mt-4 rounded-lg border border-sky-200 bg-sky-50 text-sky-900 p-4"
      data-testid="external-internal-differentiator"
    >
      <div className="flex items-start gap-3">
        <CheckCircle size={18} className="mt-0.5 shrink-0 text-sky-600" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-sm font-semibold">What your internal source says</h4>
            <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-sky-100 text-sky-800">
              {internalSources.length || 1} internal source{(internalSources.length || 1) === 1 ? '' : 's'}
              {confidencePct ? ` • ${confidencePct}%` : ''}
            </span>
          </div>
          <p className="mt-1 text-sm leading-relaxed">
            You searched the web for this — but Qflow also has approved internal guidance.
            Treat the internal answer below as the authority.
          </p>

          <div className="mt-3 rounded-md border border-sky-200 bg-white p-3 text-sm">
            <p className="leading-relaxed text-gray-800">{internalExcerpt}</p>
            {(sourceNames || extraCount > 0) && (
              <p className="mt-2 text-xs text-sky-700">
                Source: {sourceNames}{extraCount > 0 ? ` + ${extraCount} more` : ''}
              </p>
            )}
          </div>

          <p className="mt-2 text-xs font-medium opacity-90">
            If the web answer differs from this, flag it for review before changing the internal process.
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            {onSaveDraft && (
              <button
                onClick={onSaveDraft}
                className="rounded-md border border-sky-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-sky-100"
                data-testid="external-diff-save-draft"
              >
                Save as internal draft
              </button>
            )}
            <button
              onClick={onFlagClick}
              className="rounded-md border border-sky-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-sky-100"
              data-testid="external-diff-request-review"
            >
              Request internal review
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Legacy component for backwards compatibility
const KnowledgeCoverageNotice = ({ coverage, queryMode, onFlagClick }) => {
  // Only show for partial, external, or inferred coverage (not full)
  if (coverage === 'full') return null;

  const coverageMessages = {
    inferred: 'This answer was generated using inferred guidance.',
    partial: 'This answer was generated using partial internal guidance.',
    external: 'This answer includes external or supplementary guidance.',
  };

  return (
    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
      <div className="flex items-start gap-3">
        <Info size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="text-sm font-medium text-amber-900">Knowledge Coverage Notice</h4>
          <p className="text-xs text-amber-700 mt-1">
            {coverageMessages[coverage] || 'This answer was generated using available guidance.'}
          </p>
          <p className="text-xs text-amber-600 mt-1">
            You can help improve Qflow by flagging this query for internal documentation.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onFlagClick}
          className="flex-shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100"
        >
          <Flag size={14} className="mr-1" />
          Flag for Knowledge Review
        </Button>
      </div>
    </div>
  );
};

// ============= FLAG MODAL COMPONENT =============
const FlagForReviewModal = ({ isOpen, onClose, queryData, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(reason);
      setSubmitted(true);
      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setReason('');
      }, 2000);
    } catch (error) {
      toast.error('Failed to submit flag');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" aria-describedby="flag-modal-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag size={20} className="text-amber-600" />
            Flag for Knowledge Review
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="py-8 text-center">
            <CheckCircle size={48} className="mx-auto text-green-500 mb-3" />
            <h3 className="font-medium text-gray-900">Thank you!</h3>
            <p className="text-sm text-gray-600 mt-1">
              This query has been flagged for knowledge review.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Auto-captured info */}
            <div className="bg-gray-50 p-3 rounded-lg space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500">Query</span>
                <span className="text-gray-700 font-medium truncate max-w-[200px]">{queryData?.query_text}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Mode</span>
                <span className="text-gray-700 font-medium capitalize">{queryData?.query_mode || 'Standard'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Coverage</span>
                <span className="text-gray-700 font-medium capitalize">{queryData?.knowledge_coverage || 'Unknown'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Sources Used</span>
                <span className="text-gray-700 font-medium">{queryData?.matched_resolutions?.length || 0}</span>
              </div>
            </div>

            {/* Optional reason */}
            <div>
              <Label className="text-sm text-gray-700">
                Why should this be documented internally? <span className="text-gray-400">(optional)</span>
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., This is a common customer question that our team answers frequently..."
                className="mt-1.5 text-sm"
                rows={3}
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={submitting}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {submitting ? 'Submitting...' : 'Submit Flag'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============= CITATION TOOLTIP COMPONENT =============
const CitationTooltip = ({ citationId, source, onClick }) => {
  if (!source) {
    return <span className="text-blue-600 font-medium">[{citationId}]</span>;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => onClick(source)}
            className="inline-flex items-center px-1.5 py-0.5 text-xs font-semibold text-blue-700 bg-blue-100 hover:bg-blue-200 rounded transition-colors cursor-pointer border border-blue-200"
          >
            [{citationId}]
          </button>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="max-w-xs p-3 bg-white border shadow-lg rounded-lg"
        >
          <div className="space-y-1.5">
            <div className="flex items-start gap-2">
              <FileText size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <span className="font-semibold text-gray-900 text-sm leading-tight">{source.title}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Tag size={12} />
              <span>{source.source_type_label || 'Source'}</span>
            </div>
            {source.team_name && (
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <Building2 size={12} />
                <span>{source.team_name}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Clock size={12} />
              <span>{source.last_modified_display || 'Unknown'}</span>
            </div>
            {source.owner && (
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <User size={12} />
                <span>{source.owner}</span>
              </div>
            )}
            <div className="pt-1 text-[10px] text-gray-400 border-t mt-1">
              Click to view full source
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ============= SOURCE SIDE PANEL COMPONENT =============
const SourceSidePanel = ({ source, isOpen, onClose, onOpenInKnowledgeHub }) => {
  if (!isOpen || !source) return null;

  const sourceColors = {
    'Note': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
    'Resolution Guide': { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
    'Policy': { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700' },
    'Process': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
  };
  
  const sourceType = source.source_type_label || source.type || source.asset_type || 'Source';
  const sourceTitle = source.title || source.doc_title || source.name || 'Untitled source';
  const citationLabel = source.citation_id || source.id || source.doc_id || 'SRC';
  const sourceId = source.source_id || source.id || source.doc_id || citationLabel;
  const matchedEvidence = (
    source.source_quote ||
    source.matched_text ||
    source.matched_excerpt ||
    source.chunk_text ||
    source.excerpt ||
    source.answer ||
    ''
  ).trim();
  const fullSourceText = (
    source.full_content ||
    source.content ||
    source.description ||
    source.policy_content ||
    ''
  ).trim();
  const showFullSourceText = fullSourceText && fullSourceText !== matchedEvidence;
  const colors = sourceColors[sourceType] || sourceColors['Note'];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 z-40 transition-opacity"
        onClick={onClose}
      />
      
      {/* Side Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 overflow-hidden flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className={`${colors.bg} ${colors.border} border-b px-6 py-4`}>
          <div className="flex items-start justify-between">
            <div className="flex-1 pr-4">
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${colors.bg} ${colors.text} ${colors.border} border`}>
                  [{citationLabel}]
                </span>
                <span className={`text-xs px-2 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                  {sourceType}
                </span>
              </div>
              <h2 className="text-lg font-bold text-gray-900">{sourceTitle}</h2>
            </div>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          
          {/* Metadata Row */}
          <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-600">
            {source.team_name && (
              <div className="flex items-center gap-1">
                <Building2 size={12} />
                <span>{source.team_name}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Clock size={12} />
              <span>Modified: {source.last_modified_display || source.last_updated || 'Unknown'}</span>
            </div>
            {source.owner && (
              <div className="flex items-center gap-1">
                <User size={12} />
                <span>Owner: {source.owner}</span>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Matched Evidence */}
          {matchedEvidence && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={15} className="text-emerald-600" />
                Matched Evidence
              </h3>
              <div className="bg-emerald-50 border border-emerald-200 border-l-4 border-l-emerald-500 rounded-lg p-3 max-h-72 overflow-y-auto">
                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{matchedEvidence}</p>
              </div>
            </div>
          )}

          {/* Full Source Text */}
          {showFullSourceText && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Full Source Text</h3>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-96 overflow-y-auto">
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{fullSourceText}</p>
              </div>
            </div>
          )}

          {/* Description */}
          {source.description && source.description.trim() !== matchedEvidence && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{source.description}</p>
            </div>
          )}

          {/* Resolution Steps */}
          {source.resolution_steps && source.resolution_steps.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Steps</h3>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                {source.resolution_steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>
          )}

          {/* Policy Content */}
          {source.policy_content && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Policy Content</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{source.policy_content}</p>
            </div>
          )}

          {/* Contacts */}
          {source.contacts && source.contacts.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Contacts</h3>
              <div className="flex flex-wrap gap-2">
                {source.contacts.map((contact, i) => (
                  <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                    {contact}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Email Template */}
          {source.email_template && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Mail size={14} />
                Email Template
              </h3>
              <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-800 whitespace-pre-wrap font-mono">
                {source.email_template}
              </div>
            </div>
          )}

          {/* SLA Info */}
          {(source.sla_hours || source.ticket_reference) && (
            <div className="grid grid-cols-2 gap-4">
              {source.sla_hours && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm">SLA</h3>
                  <p className="text-sm text-gray-700">{source.sla_hours} hours</p>
                </div>
              )}
              {source.ticket_reference && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm">Ticket Reference</h3>
                  <p className="text-sm text-gray-700">{source.ticket_reference}</p>
                </div>
              )}
            </div>
          )}

          {/* Keywords */}
          {source.keywords && source.keywords.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Keywords</h3>
              <div className="flex flex-wrap gap-1">
                {source.keywords.map((kw, i) => (
                  <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Match Score */}
          {typeof source.match_percent === 'number' && (
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Match Relevance</span>
                <span className="font-semibold text-blue-700">{source.match_percent}%</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
            <span>Source ID: {String(sourceId || 'N/A').substring(0, 12)}</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => onOpenInKnowledgeHub?.(source)}
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
              >
                <ExternalLink size={12} />
                Open in Knowledge Hub
              </button>
              <button 
                onClick={onClose}
                className="text-gray-600 hover:text-gray-800 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ============= TEXT WITH CITATIONS COMPONENT =============
const TextWithCitations = ({ text, sources, onCitationClick }) => {
  // Build a map of citation_id to source
  const sourceMap = useMemo(() => {
    const map = {};
    (sources || []).forEach(src => {
      if (src.citation_id) {
        map[src.citation_id] = src;
      }
    });
    return map;
  }, [sources]);

  // Parse text and replace citations with interactive components
  const renderTextWithCitations = useCallback(() => {
    if (!text) return null;
    
    const parts = [];
    let lastIndex = 0;
    let match;
    
    // Reset regex
    const regex = new RegExp(CITATION_PATTERN.source, 'g');
    
    while ((match = regex.exec(text)) !== null) {
      // Add text before citation
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {text.slice(lastIndex, match.index)}
          </span>
        );
      }
      
      // Add citation component
      const citationId = match[1];
      const source = sourceMap[citationId];
      parts.push(
        <CitationTooltip
          key={`citation-${match.index}`}
          citationId={citationId}
          source={source}
          onClick={onCitationClick}
        />
      );
      
      lastIndex = regex.lastIndex;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(
        <span key={`text-${lastIndex}`}>
          {text.slice(lastIndex)}
        </span>
      );
    }
    
    return parts;
  }, [text, sourceMap, onCitationClick]);

  return <>{renderTextWithCitations()}</>;
};

// ============= REFERENCED SOURCES SECTION =============
const ReferencedSourcesSection = ({ sources, onSourceClick }) => {
  // Get unique sources by citation_id - moved before early return
  const uniqueSources = useMemo(() => {
    if (!sources || sources.length === 0) return [];
    const seen = new Set();
    return sources.filter(src => {
      if (!src.citation_id || seen.has(src.citation_id)) return false;
      seen.add(src.citation_id);
      return true;
    });
  }, [sources]);

  if (uniqueSources.length === 0) return null;

  const sourceColors = {
    'Note': { bg: 'bg-blue-50', border: 'border-l-blue-400', text: 'text-blue-700', badge: 'bg-blue-100' },
    'Resolution Guide': { bg: 'bg-emerald-50', border: 'border-l-emerald-400', text: 'text-emerald-700', badge: 'bg-emerald-100' },
    'Policy': { bg: 'bg-purple-50', border: 'border-l-purple-400', text: 'text-purple-700', badge: 'bg-purple-100' },
    'Process': { bg: 'bg-amber-50', border: 'border-l-amber-400', text: 'text-amber-700', badge: 'bg-amber-100' },
  };

  return (
    <div className="mt-6 pt-4 border-t border-gray-200">
      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <FileText size={16} />
        Referenced Sources ({uniqueSources.length})
      </h3>
      <div className="space-y-2">
        {uniqueSources.map((source) => {
          const colors = sourceColors[source.source_type_label] || sourceColors['Note'];
          const today = new Date();
          const effectiveFrom = source.effective_from ? new Date(`${source.effective_from}T00:00:00`) : null;
          const effectiveUntil = source.effective_until ? new Date(`${source.effective_until}T23:59:59`) : null;
          const freshness = effectiveFrom && effectiveFrom > today
            ? { label: 'Upcoming', cls: 'bg-blue-100 text-blue-700' }
            : effectiveUntil && effectiveUntil < today
              ? { label: 'Expired', cls: 'bg-rose-100 text-rose-700' }
              : (effectiveFrom || effectiveUntil)
                ? { label: 'Current', cls: 'bg-emerald-100 text-emerald-700' }
                : null;
          return (
            <button
              key={source.citation_id}
              onClick={() => onSourceClick(source)}
              className={`w-full text-left p-3 rounded-lg border border-l-4 ${colors.border} ${colors.bg} hover:shadow-sm transition-all`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-bold ${colors.text}`}>
                      [{source.citation_id}]
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${colors.badge} ${colors.text}`}>
                      {source.source_type_label}
                    </span>
                  </div>
                  <h4 className="font-medium text-gray-900 text-sm truncate">{source.title}</h4>
                  {(source.badges || []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {source.badges.map(badge => (
                        <span
                          key={badge.id}
                          className="rounded-full border px-2 py-0.5 text-[10px] font-medium"
                          style={{ borderColor: badge.color, color: badge.color, backgroundColor: `${badge.color}12` }}
                        >
                          {badge.name}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    {source.team_name && <span>{source.team_name}</span>}
                    <span>• {source.last_modified_display || 'Unknown'}</span>
                    {source.owner && <span>• {source.owner}</span>}
                    {source.version_label && <span>• v{source.version_label}</span>}
                    {freshness && <span className={`rounded-full px-1.5 py-0.5 ${freshness.cls}`}>{freshness.label}</span>}
                  </div>
                </div>
                <ExternalLink size={14} className="text-gray-400 flex-shrink-0" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============= MAIN COMPONENT =============
const QuerySubmit = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Home's Ask box hands its question over in navigation state.
  const [queryText, setQueryText] = useState(() => (typeof location.state?.question === 'string' ? location.state.question.slice(0, 2000) : ''));
  const [queryError, setQueryError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const queryInputRef = useRef(null);
  const handledQuestionKey = useRef(null);
  const queryFormRef = useRef(null);
  const openFloorSpaceWithQuestion = () => navigate('/floor-space', { state: { draftQuestion: queryText.trim() } });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [queryMode, setQueryMode] = useState('standard');
  const [companyVerificationEnabled, setCompanyVerificationEnabled] = useState(false);
  const [combinedSearchEnabled, setCombinedSearchEnabled] = useState(false);
  const [knowledgeUpdatePolicy, setKnowledgeUpdatePolicy] = useState('review');
  const [queryModePolicyError, setQueryModePolicyError] = useState(false);
  
  // Refinement state
  const [refinementText, setRefinementText] = useState('');
  const [refinementCount, setRefinementCount] = useState(0);
  const [showRefinement, setShowRefinement] = useState(false);
  const [refinementPlaceholder, setRefinementPlaceholder] = useState('Add missing details or clarify the scenario (optional)');
  const [originalQuery, setOriginalQuery] = useState('');
  const [queryHistory, setQueryHistory] = useState([]);
  const MAX_REFINEMENTS = 2;

  // Nudge card state (proactive query-refinement coach)
  const [nudge, setNudge] = useState(null);
  const [nudgeLoading, setNudgeLoading] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [nudgeExpanded, setNudgeExpanded] = useState(false);

  // Answer view mode state
  const [viewMode, setViewMode] = useState('concise');
  const [showFullConfirmation, setShowFullConfirmation] = useState(false);

  // Knowledge flag modal state
  const [isFlagModalOpen, setIsFlagModalOpen] = useState(false);

  // Side panel state
  const [selectedSource, setSelectedSource] = useState(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  // Get pre-generated concise and full answers from backend
  const answerConcise = results?.answer_concise || '';
  const answerFull = results?.answer_full || results?.answer || '';
  
  // Check if toggle should be disabled (both versions are the same or very short)
  const isContentBrief = useMemo(() => {
    if (!answerConcise || !answerFull) return true;
    return answerConcise === answerFull || answerFull.length < 100;
  }, [answerConcise, answerFull]);

  // Display the selected version
  const displayedAnswer = viewMode === 'full' ? answerFull : (answerConcise || answerFull);

  const sourceLookup = useMemo(() => {
    const map = new Map();
    const addSource = (source) => {
      if (!source) return;
      [
        source.citation_id,
        source.id,
        source.source_id,
        source.doc_id,
        source.title,
        source.doc_title,
      ].filter(Boolean).forEach((key) => map.set(String(key), source));
    };

    (results?.matched_resolutions || []).forEach(addSource);
    (results?.decision_response?.sources || []).forEach(addSource);
    (results?.decision_response?.grounding?.sources || []).forEach(addSource);
    return map;
  }, [results]);

  const resolveSourceDetails = useCallback((source) => {
    if (!source) return null;
    const matchKeys = [
      source.citation_id,
      source.id,
      source.source_id,
      source.doc_id,
      source.title,
      source.doc_title,
    ].filter(Boolean).map(String);

    const richerSource = matchKeys.map((key) => sourceLookup.get(key)).find(Boolean);
    return richerSource ? { ...richerSource, ...source } : source;
  }, [sourceLookup]);

  // Handle view mode change
  const handleViewModeChange = (mode) => {
    if (mode === viewMode || isContentBrief) return;
    
    setViewMode(mode);
    
    if (mode === 'full') {
      setShowFullConfirmation(true);
      setTimeout(() => setShowFullConfirmation(false), 2000);
    }
  };

  // Handle citation click - open side panel
  const handleCitationClick = useCallback((source) => {
    setSelectedSource(resolveSourceDetails(source));
    setIsSidePanelOpen(true);
  }, [resolveSourceDetails]);

  const handleOpenSourceInKnowledgeHub = useCallback((source) => {
    const sourceId = source?.source_id || source?.id || source?.doc_id;
    navigate(sourceId ? `/resolutions?source=${encodeURIComponent(sourceId)}` : '/resolutions');
  }, [navigate]);

  // Close side panel
  const handleCloseSidePanel = useCallback(() => {
    setIsSidePanelOpen(false);
    setTimeout(() => setSelectedSource(null), 300); // Clear after animation
  }, []);

  // Handle flag submission for knowledge review
  const handleFlagSubmit = useCallback(async (reason) => {
    if (!results) return;
    
    try {
      await axiosInstance.post('/knowledge-flags', {
        query_id: results.id,
        query_text: results.query_text,
        mode_used: queryMode || 'standard',
        knowledge_coverage: results.knowledge_coverage || 'unknown',
        sources_used: results.matched_resolutions?.map(r => r.title) || [],
        reason: reason || null
      });
      toast.success('Query flagged for knowledge review');
    } catch (error) {
      console.error('Error flagging query:', error);
      throw error;
    }
  }, [results, queryMode]);

  // Handle save web response as internal draft
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const handleSaveDraft = useCallback(async () => {
    if (!results?.is_web_response || isDraftSaving || draftSaved) return;
    
    setIsDraftSaving(true);
    try {
      const title = results.structured_response?.title || results.query_text || 'Untitled Draft';
      await axiosInstance.post('/knowledge-drafts', {
        query_id: results.id,
        title: title.substring(0, 200),
        notes: `Suggested from a cited web response to: "${results.query_text}"`,
      });
      setDraftSaved(true);
      toast.success('Saved as internal draft for review');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setIsDraftSaving(false);
    }
  }, [results, isDraftSaving, draftSaved]);

  useEffect(() => {
    if (typeof location.state?.question !== 'string' || handledQuestionKey.current === location.key) return;
    handledQuestionKey.current = location.key;
    // Quick ask also works when this route is already mounted.
    setQueryText(location.state.question.slice(0, 2000));
    // Quick ask submits; other hand-overs only pre-fill the question.
    const askNow = location.state?.autoSubmit === true && location.state.question.trim().length > 0;
    // Clear the handed-over question so a refresh or Back does not restore it.
    navigate(location.pathname, { replace: true, state: null });
    window.requestAnimationFrame(() => {
      resizeQueryInput(queryInputRef.current);
      queryInputRef.current?.focus();
      if (askNow) queryFormRef.current?.requestSubmit();
    });
  }, [location.key]);

  // Web Mode is an organisation policy, not a user preference. Refresh it so
  // changes made by one tenant administrator reach already-open user sessions.
  useEffect(() => {
    let active = true;
    const fetchSettings = async () => {
      try {
        const res = await axiosInstance.get('/settings/query-modes');
        if (!active) return;
        const webEnabled = res.data?.web_enabled === true;
        const combinedEnabled = webEnabled && res.data?.combined_enabled === true;
        setCompanyVerificationEnabled(webEnabled);
        setCombinedSearchEnabled(combinedEnabled);
        setKnowledgeUpdatePolicy(res.data?.knowledge_update_policy || 'review');
        setQueryModePolicyError(false);
        setQueryMode(current => (!webEnabled && current !== 'standard') || (!combinedEnabled && current === 'internal_and_web') ? 'standard' : current);
      } catch (error) {
        if (!active) return;
        // Never invent a permissive organisation policy. Internal search stays
        // available while Qflow retries the authoritative tenant setting.
        setCompanyVerificationEnabled(false);
        setCombinedSearchEnabled(false);
        setKnowledgeUpdatePolicy('review');
        setQueryMode('standard');
        setQueryModePolicyError(true);
      }
    };
    fetchSettings();
    const timer = window.setInterval(fetchSettings, 30000);
    window.addEventListener('focus', fetchSettings);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', fetchSettings);
    };
  }, []);

  const resizeQueryInput = useCallback((element) => {
    if (!element) return;
    element.style.height = 'auto';
    const maxHeight = 8 * 24 + 24;
    const nextHeight = Math.min(Math.max(element.scrollHeight, 56), maxHeight);
    element.style.height = `${nextHeight}px`;
    element.style.overflowY = element.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  const handleQueryChange = (event) => {
    setQueryText(event.target.value);
    setQueryError('');
    setSubmitError('');
    resizeQueryInput(event.target);
  };

  const handleQueryKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent?.isComposing) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!queryText.trim()) {
      setQueryError('Enter a question first');
      queryInputRef.current?.focus();
      return;
    }

    setQueryError('');
    setSubmitError('');
    setLoading(true);
    setNudge(null);
    setNudgeDismissed(false);
    setNudgeExpanded(false);
    try {
      const res = await axiosInstance.post('/queries', {
        query_text: queryText,
        mode: queryMode,
      });
      setResults(res.data);
      setOriginalQuery(queryText);
      setQueryHistory([queryText]);
      setRefinementCount(0);
      setDraftSaved(false);
      setShowRefinement(false);
      setRefinementText('');
      setViewMode('concise');
      setShowFullConfirmation(false);
      // Fire-and-forget nudge lookup (non-blocking). Standard mode only —
      // web mode already exposes the differentiator comparison card.
      if (queryMode === 'standard') {
        fetchNudges(queryText, res.data?.has_good_match === true);
      }
    } catch (error) {
      setSubmitError('Qflow could not complete the search. Check your connection and try again.');
      toast.error('Qflow could not complete the search');
    } finally {
      setLoading(false);
    }
  };

  const fetchNudges = async (text, hadMatch) => {
    setNudgeLoading(true);
    try {
      const res = await axiosInstance.post('/queries/nudge', {
        query_text: text,
        had_match: hadMatch,
      });
      const data = res.data || {};
      // Only surface the card if there is something useful to show.
      if ((data.insight && data.insight.trim()) || (data.suggestions && data.suggestions.length > 0)) {
        setNudge(data);
      } else {
        setNudge(null);
      }
    } catch (error) {
      setNudge(null);
    } finally {
      setNudgeLoading(false);
    }
  };

  const applyNudge = async (refinedQuery) => {
    if (!refinedQuery || !refinedQuery.trim()) return;
    setQueryText(refinedQuery);
    setNudge(null);
    setNudgeDismissed(false);
    setNudgeExpanded(false);
    setQueryError('');
    setSubmitError('');
    setLoading(true);
    try {
      const res = await axiosInstance.post('/queries', {
        query_text: refinedQuery,
        mode: queryMode,
      });
      setResults(res.data);
      setOriginalQuery(refinedQuery);
      setQueryHistory([refinedQuery]);
      setRefinementCount(0);
      setDraftSaved(false);
      setShowRefinement(false);
      setRefinementText('');
      setViewMode('concise');
      setShowFullConfirmation(false);
      toast.success('Refined query submitted!');
      if (queryMode === 'standard') {
        fetchNudges(refinedQuery, res.data?.has_good_match === true);
      }
    } catch (error) {
      setSubmitError('Qflow could not complete the refined search. Try again.');
      toast.error('Qflow could not complete the refined search');
    } finally {
      setLoading(false);
    }
  };

  const handleWebSearch = async () => {
    if (!queryText.trim()) {
      setQueryError('Enter a question first');
      queryInputRef.current?.focus();
      return;
    }

    const webMode = 'company_verification';
    setQueryMode(webMode);
    setQueryError('');
    setSubmitError('');
    setLoading(true);
    setNudge(null);
    setNudgeDismissed(false);
    setNudgeExpanded(false);
    try {
      const res = await axiosInstance.post('/queries', {
        query_text: queryText,
        mode: webMode,
      });
      setResults(res.data);
      setOriginalQuery(queryText);
      setQueryHistory([queryText]);
      setRefinementCount(0);
      setDraftSaved(false);
      setShowRefinement(false);
      setRefinementText('');
      setViewMode('concise');
      setShowFullConfirmation(false);
    } catch (error) {
      setSubmitError('Qflow could not complete the web search. Check your connection and try again.');
      toast.error('Qflow could not complete the web search');
    } finally {
      setLoading(false);
    }
  };

  const handleRefinementSubmit = async (e) => {
    e.preventDefault();
    if (!refinementText.trim()) {
      toast.error('Please enter a refinement');
      return;
    }

    setLoading(true);
    try {
      const refinedQueryText = `Original Query: ${originalQuery}\n\nRefinement: ${refinementText}`;
      const res = await axiosInstance.post('/queries', {
        query_text: refinedQueryText,
        mode: queryMode,
      });
      setResults(res.data);
      setQueryHistory([...queryHistory, refinementText]);
      setRefinementCount(refinementCount + 1);
      setRefinementText('');
      setShowRefinement(false);
      setRefinementPlaceholder('Add missing details or clarify the scenario (optional)');
      setViewMode('concise');
      setShowFullConfirmation(false);
      toast.success('Answer updated with your refinement!');
    } catch (error) {
      toast.error('Qflow could not update the answer. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewQuery = () => {
    setQueryText('');
    setQueryError('');
    setSubmitError('');
    setResults(null);
    setNudge(null);
    setNudgeDismissed(false);
    setNudgeExpanded(false);
    setRefinementCount(0);
    setShowRefinement(false);
    setRefinementText('');
    setOriginalQuery('');
    setQueryHistory([]);
    setRefinementPlaceholder('Add missing details or clarify the scenario (optional)');
    setViewMode('concise');
    setShowFullConfirmation(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.requestAnimationFrame(() => {
      resizeQueryInput(queryInputRef.current);
      queryInputRef.current?.focus();
    });
  };

  const setRefinementType = (type) => {
    const placeholders = {
      detail: 'Add missing details (e.g., customer type, specific product, timeline)',
      scenario: 'Describe your specific scenario or situation',
      followup: 'Ask a follow-up question about the answer',
    };
    setRefinementPlaceholder(placeholders[type] || refinementPlaceholder);
    setShowRefinement(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-950">Ask Qflow</h1>
          <p className="mt-1 text-sm text-slate-600">Ask a question and get a grounded answer from the knowledge you can access.</p>
        </div>

        {/* Composer: existing query contract, simplified visual hierarchy. */}
        <Card className="mb-6 overflow-hidden border border-slate-200 bg-white shadow-none">
          <form ref={queryFormRef} onSubmit={handleSubmit}>
            <div className="px-5 pb-4 pt-5 sm:px-6">
              <Label htmlFor="query" className="sr-only">Ask Qflow a question</Label>
              <Textarea
                ref={queryInputRef}
                id="query"
                rows={2}
                data-testid="query-input"
                value={queryText}
                onChange={handleQueryChange}
                onKeyDown={handleQueryKeyDown}
                aria-invalid={Boolean(queryError)}
                aria-describedby={queryError ? 'query-error' : undefined}
                placeholder="Ask about a process, policy, decision or next step"
                className="min-h-[56px] resize-none border-0 bg-transparent p-0 text-base leading-6 shadow-none placeholder:text-slate-400 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <div id="query-error" aria-live="polite" className="mt-2 min-h-5 text-sm text-rose-600">
                {queryError}
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <div className="inline-flex w-fit max-w-full flex-wrap rounded-lg border border-slate-200 bg-white p-1" role="group" aria-label="Answer sources">
                <button
                  type="button"
                  aria-pressed={queryMode === 'standard'}
                  onClick={() => setQueryMode('standard')}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                    queryMode === 'standard'
                      ? 'bg-sky-600 text-white'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  Internal only
                </button>
                {companyVerificationEnabled && (
                  <button
                    type="button"
                    aria-pressed={queryMode === 'company_verification'}
                    onClick={() => setQueryMode('company_verification')}
                    className={`rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                      queryMode === 'company_verification'
                        ? 'bg-sky-600 text-white'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    Web sources
                  </button>
                )}
                {companyVerificationEnabled && combinedSearchEnabled && (
                  <button
                    type="button"
                    data-testid="internal-and-web-mode-btn"
                    aria-pressed={queryMode === 'internal_and_web'}
                    aria-label="Internal and web sources"
                    title="Search approved internal knowledge first, then use configured web sources to complete gaps"
                    onClick={() => setQueryMode('internal_and_web')}
                    className={`rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                      queryMode === 'internal_and_web'
                        ? 'bg-sky-600 text-white'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    Internal + web
                  </button>
                )}
              </div>
              {queryModePolicyError && <p className="text-xs text-amber-700" role="status">Web Mode is unavailable while Qflow reloads your organisation policy. Internal search remains available.</p>}

              <div className="flex items-center justify-end gap-2">
                <span className="mr-1 text-xs text-slate-500">Enter to send · Shift+Enter for a new line</span>
                {results && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleNewQuery}
                    className="border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                    data-testid="start-new-query-btn"
                  >
                    <RefreshCw size={15} className="mr-2" aria-hidden="true" />
                    New question
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-sky-600 px-5 text-white hover:bg-sky-700 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
                  data-testid="submit-query-btn"
                >
                  {loading ? <RefreshCw className="mr-2 animate-spin" size={16} aria-hidden="true" /> : <Search className="mr-2" size={16} aria-hidden="true" />}
                  {loading ? 'Searching' : 'Ask'}
                </Button>
              </div>
            </div>
          </form>
        </Card>

        {submitError && (
          <div className="mb-5 flex items-center justify-between gap-4 rounded-xl border border-rose-200 bg-white px-4 py-3" role="alert">
            <p className="text-sm text-slate-700">{submitError}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => queryInputRef.current?.form?.requestSubmit()}>
              Retry
            </Button>
          </div>
        )}

        {loading && (
          <Card className="mb-6 border border-slate-200 bg-white p-6 shadow-none" role="status" aria-live="polite" data-testid="query-loading-state">
            <span className="sr-only">Searching Qflow knowledge</span>
            <div className="animate-pulse space-y-4" aria-hidden="true">
              <div className="h-4 w-32 rounded bg-slate-200" />
              <div className="space-y-2">
                <div className="h-4 w-full rounded bg-slate-200" />
                <div className="h-4 w-5/6 rounded bg-slate-200" />
                <div className="h-4 w-2/3 rounded bg-slate-200" />
              </div>
              <div className="h-16 rounded-lg border border-slate-200 bg-slate-50" />
            </div>
          </Card>
        )}

        {/* Nudge Card — compact, collapsible query-refinement coach (standard mode) */}
        {!nudgeLoading && nudge && !nudgeDismissed
          && !(results && !results.no_internal_source
            && results.validation?.validation_status === 'grounded'
            && results.resolution_confidence >= 0.75)
          && (nudge.insight || (nudge.suggestions && nudge.suggestions.length > 0)) && (
          <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50/70" data-testid="nudge-card">
            {/* Slim header row — minimal footprint so it never clouds the resolution */}
            <div className="flex items-center gap-2.5 px-3 py-2">
              <Lightbulb className="h-4 w-4 flex-shrink-0 text-amber-500" />
              <p className="min-w-0 flex-1 text-xs leading-5 text-amber-900 sm:text-sm">
                <span className="font-semibold">Tip:</span> {nudge.insight || 'You can sharpen this question for a better answer.'}
              </p>
              {nudge.suggestions && nudge.suggestions.length > 0 && (
                <button
                  type="button"
                  onClick={() => setNudgeExpanded(v => !v)}
                  className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900"
                  data-testid="nudge-toggle"
                >
                  {nudgeExpanded ? 'Hide' : `Refine (${nudge.suggestions.length})`}
                  {nudgeExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              )}
              <button
                type="button"
                onClick={() => setNudgeDismissed(true)}
                className="flex-shrink-0 text-amber-400 hover:text-amber-700"
                aria-label="Dismiss"
                data-testid="nudge-dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Suggestions — hidden until expanded */}
            {nudgeExpanded && nudge.suggestions && nudge.suggestions.length > 0 && (
              <div className="space-y-2 border-t border-amber-200/70 px-3 pb-3 pt-2">
                {nudge.suggestions.map((s, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => applyNudge(s.refined_query)}
                    disabled={loading}
                    className="group flex w-full items-start gap-2.5 rounded-lg border border-amber-200 bg-white p-2.5 text-left transition-all hover:border-amber-400 hover:shadow-sm disabled:opacity-50"
                    data-testid={`nudge-suggestion-${idx}`}
                  >
                    <ArrowRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500 transition-transform group-hover:translate-x-0.5" />
                    <div>
                      {s.label && (
                        <span className="block text-xs font-semibold text-amber-900">{s.label}</span>
                      )}
                      <span className="block text-sm text-gray-700 mt-0.5">{s.refined_query}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Results */}
        {!loading && results && (
          <div className="space-y-6" aria-live="polite" data-testid="query-result-state">
            {/* ACCESS RESTRICTED STATE — content exists but user lacks access */}
            {results.access_restricted && (
              <AccessRestrictedState onFloorSpaceClick={openFloorSpaceWithQuestion} />
            )}

            {/* NO ANSWER STATE — only for standard mode when no internal source AND not restricted */}
            {results.no_internal_source && !results.is_web_response && !results.access_restricted && (
              <NoAnswerState
                results={results}
                queryMode={queryMode}
                webEnabled={companyVerificationEnabled}
                onFlagClick={() => setIsFlagModalOpen(true)}
                onFloorSpaceClick={openFloorSpaceWithQuestion}
                onWebSearch={handleWebSearch}
              />
            )}

            {/* AI Answer with Citations — render for: internal matches OR web responses (never for restricted) */}
            {(!results.access_restricted && (!results.no_internal_source || results.is_web_response) && (results.ai_suggestions || results.answer || results.structured_response)) && (
              <Card className={`p-6 ${results.is_web_response ? 'bg-gradient-to-br from-indigo-50/50 to-slate-50 border border-indigo-200/60' : 'bg-gradient-to-br from-blue-50 to-sky-50 border-blue-200'}`}>
                {/* View Mode Toggle - Above Answer */}
                <div className={`flex items-center justify-between mb-4 pb-4 border-b ${results.is_web_response ? 'border-indigo-100' : 'border-blue-100'}`}>
                  <h2 className="text-xl font-semibold flex items-center space-x-2">
                    {results.is_web_response ? (
                      <Globe className="text-indigo-500" size={22} />
                    ) : (
                      <AlertCircle className="text-blue-600" size={24} />
                    )}
                    <span>{results.is_combined_response ? 'Internal and web' : results.is_web_response ? 'Web Response' : 'Resolution'}</span>
                    {results.is_web_response && (
                      <span className="text-xs px-2.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium flex items-center gap-1">
                        <Globe size={11} />
                        {results.is_combined_response ? 'Combined sources' : 'External guidance'}
                      </span>
                    )}
                    {(!results.is_web_response || results.is_combined_response) && results.resolution_confidence > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        results.confidence_mode === 'operational'
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {results.confidence_mode === 'operational'
                          ? 'High evidence'
                          : results.confidence_mode === 'diagnostic'
                            ? 'Medium evidence'
                            : 'Low evidence'}
                        {isAdminOrPlatform(user)
                          ? ` · ${Math.round(results.resolution_confidence * 100)}%`
                          : ''}
                      </span>
                    )}
                  </h2>
                  
                  {/* View Toggle - Only show for legacy mode when structured_response is not available */}
                  {!results.structured_response && !isContentBrief ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">View:</span>
                      <div className="inline-flex p-1 bg-gray-100 rounded-lg">
                        {/* Concise (Operational) */}
                        <button
                          onClick={() => handleViewModeChange('concise')}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                            viewMode === 'concise'
                              ? 'bg-white text-sky-700 shadow-sm'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${
                            viewMode === 'concise' ? 'border-sky-500' : 'border-gray-300'
                          }`}>
                            {viewMode === 'concise' && <div className="w-1.5 h-1.5 rounded-full bg-sky-500" />}
                          </div>
                          Concise
                        </button>
                        
                        {/* Full (Diagnostic) */}
                        <button
                          onClick={() => handleViewModeChange('full')}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                            viewMode === 'full'
                              ? 'bg-white text-purple-700 shadow-sm'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${
                            viewMode === 'full' ? 'border-purple-500' : 'border-gray-300'
                          }`}>
                            {viewMode === 'full' && <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />}
                          </div>
                          Full
                        </button>
                      </div>
                    </div>
                  ) : !results.structured_response ? (
                    <div className="text-xs text-gray-400 italic">
                      Single view available
                    </div>
                  ) : null}
                </div>
                
                {/* PRIMARY: Decision-First Response */}
                {results.decision_response?.direct_answer ? (
                  <div data-testid="decision-answer">
                    <DecisionResponse
                      decision={results.decision_response}
                      queryFocus={results.query_focus}
                      userRole={user?.role}
                      currentUser={user}
                      isWebResponse={results.is_web_response}
                      isCombinedResponse={results.is_combined_response}
                      confidenceMode={results.confidence_mode}
                      confidenceFactors={results.confidence_factors}
                      onSourceClick={handleCitationClick}
                      onAskFloorSpace={openFloorSpaceWithQuestion}
                    />
                  </div>
                ) : results.structured_response ? (
                  <div className="prose prose-sm max-w-none" data-testid="structured-answer">
                    <StructuredResponse 
                      responseFull={results.structured_response}
                      responseConcise={results.structured_response_concise}
                      viewMetadata={results.view_metadata}
                      confidenceWarning={results.is_web_response && !results.is_combined_response ? null : results.confidence_warning}
                      confidenceMode={results.is_web_response && !results.is_combined_response ? null : results.confidence_mode}
                      defaultView="concise"
                    />
                  </div>
                ) : (
                  /* FALLBACK: Legacy Answer with Interactive Citations */
                  <div className="prose prose-sm max-w-none space-y-4">
                    <div className="text-gray-800 whitespace-pre-wrap leading-relaxed" data-testid="ai-suggestions" style={{ fontFamily: 'Inter, sans-serif' }}>
                      <TextWithCitations 
                        text={displayedAnswer}
                        sources={results.matched_resolutions}
                        onCitationClick={handleCitationClick}
                      />
                    </div>
                  </div>
                )}

                {!!results.quick_actions?.length && (
                  <div className="flex flex-wrap gap-2 pt-2" data-testid="response-quick-actions">
                    {results.quick_actions.map((action, index) => {
                      const external = /^https?:\/\//i.test(action.link);
                      return (
                        <a
                          key={`${action.label}-${index}`}
                          href={action.link}
                          target={external ? '_blank' : undefined}
                          rel={external ? 'noopener noreferrer' : undefined}
                          className="inline-flex items-center rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-800 hover:bg-sky-100"
                        >
                          {action.label}
                        </a>
                      );
                    })}
                  </div>
                )}
                {/* Referenced Sources Section — only show for internal matches with confidence > 0 */}
                {(!results.is_web_response || results.is_combined_response) && results.resolution_confidence > 0 && (
                  <ReferencedSourcesSection 
                    sources={results.matched_resolutions}
                    onSourceClick={handleCitationClick}
                  />
                )}

                {/* Web Source Attribution — shows for web-enabled responses */}
                <WebSourceAttribution
                  results={results}
                  onFlagClick={() => setIsFlagModalOpen(true)}
                  onSaveDraft={
                    knowledgeUpdatePolicy === 'review' && results.web_gap_detected === true
                      ? handleSaveDraft
                      : null
                  }
                  knowledgeUpdatePolicy={knowledgeUpdatePolicy}
                />

                <ExternalInternalDifferentiator
                  results={results}
                  onFlagClick={() => setIsFlagModalOpen(true)}
                  onSaveDraft={
                    knowledgeUpdatePolicy === 'review' && results.web_gap_detected === true
                      ? handleSaveDraft
                      : null
                  }
                />

                {/* Legacy Knowledge Coverage Notice - shows for partial/external coverage */}
                {!results.no_internal_source && !results.is_web_response && (
                  <KnowledgeCoverageNotice
                    coverage={results.knowledge_coverage}
                    queryMode={queryMode}
                    onFlagClick={() => setIsFlagModalOpen(true)}
                  />
                )}

                {/* Resolution confirmation appears with the answer; unanswered prompts remain pending for Dashboard reminders. */}
                {results.id && (
                  <ResolutionConfirmation
                    queryId={results.id}
                    status={results.status || 'pending_feedback'}
                    onUpdated={(updated) => setResults((current) => ({ ...current, ...updated }))}
                  />
                )}

                {/* Query Feedback - answer quality signal */}
                {results.id && (
                  <QueryFeedback queryId={results.id} />
                )}
              </Card>
            )}

            {/* Refinement Section */}
            {results && results.has_good_match !== false && refinementCount < MAX_REFINEMENTS && (
              <Card className="p-4 bg-gray-50 border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <MessageCircle size={18} className="text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">
                      Need to refine? ({MAX_REFINEMENTS - refinementCount} remaining)
                    </span>
                  </div>
                  {!showRefinement && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRefinement(true)}
                    >
                      <Plus size={14} className="mr-1" /> Add Context
                    </Button>
                  )}
                </div>

                {showRefinement && (
                  <form onSubmit={handleRefinementSubmit} className="space-y-3">
                    {/* Refinement Type Buttons */}
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => setRefinementType('detail')}
                        className="px-3 py-1 text-xs rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
                      >
                        + Add Details
                      </button>
                      <button
                        type="button"
                        onClick={() => setRefinementType('scenario')}
                        className="px-3 py-1 text-xs rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
                      >
                        + Specific Scenario
                      </button>
                      <button
                        type="button"
                        onClick={() => setRefinementType('followup')}
                        className="px-3 py-1 text-xs rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
                      >
                        + Follow-up
                      </button>
                    </div>

                    <Textarea
                      value={refinementText}
                      onChange={(e) => setRefinementText(e.target.value)}
                      placeholder={refinementPlaceholder}
                      className="min-h-[80px] text-sm"
                    />
                    <p className="text-xs text-gray-500">
                      This will update the original question — it won&apos;t start a new one.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowRefinement(false);
                          setRefinementText('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        size="sm"
                        disabled={loading || !refinementText.trim()}
                        className="bg-sky-600 hover:bg-sky-700"
                      >
                        {loading ? 'Updating...' : 'Refine Answer'}
                      </Button>
                    </div>
                  </form>
                )}
              </Card>
            )}

            {/* Floor Space Escalation Card — hide when NoAnswerState already shows it */}
            {!results.no_internal_source && (
              <div className="px-4 py-3 bg-sky-50 border border-sky-100 rounded-lg flex items-center justify-between">
                <span className="text-sm text-gray-700">
                  Need more clarity? Verify via Floor Space or Floor Leaders
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={openFloorSpaceWithQuestion}
                  className="text-sky-600 hover:text-sky-700 hover:bg-sky-100"
                >
                  Go <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
            )}

          </div>
        )}
      </div>

      {/* Flag for Knowledge Review Modal */}
      <FlagForReviewModal
        isOpen={isFlagModalOpen}
        onClose={() => setIsFlagModalOpen(false)}
        queryData={results}
        onSubmit={handleFlagSubmit}
      />

      {/* Source Side Panel */}
      <SourceSidePanel 
        source={selectedSource}
        isOpen={isSidePanelOpen}
        onClose={handleCloseSidePanel}
        onOpenInKnowledgeHub={handleOpenSourceInKnowledgeHub}
      />
    </div>
  );
};

export default QuerySubmit;
